<template>
  <v-card class="havet-editor pa-5" :class="theme" :elevation="maxmimizedUI ? 0 : 2">
    <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <slot></slot>
    <template v-if="editor">
      <div
        v-if="!maxmimizedUI"
        class="mb-5"
        :class="[small ? 'editor-menu-bar-small' : 'editor-menu-bar']"
      >
        <v-btn-toggle dense light borderless>
          <v-btn
            :small="small"
            v-for="b in buttonGroupOne"
            :title="b.title"
            :class="{
              'v-item--active v-btn--active':
                'isActive' in b && editor.isActive(b.isActive.name, b.isActive.params),
            }"
            :key="b.id"
            @click="runCommand(b.command)"
          >
            <v-icon>{{ b.icon }}</v-icon>
          </v-btn>
        </v-btn-toggle>
        <v-btn-toggle dense light borderless>
          <v-btn @click="addImageMedia" title="Bild" :small="small">
            <v-icon>mdi-multimedia</v-icon>
          </v-btn>
          <v-btn
            :small="small"
            v-for="b in buttonGroupTwo"
            :title="b.title"
            :class="{
              'v-item--active v-btn--active':
                'isActive' in b && editor.isActive(b.isActive.name, b.isActive.params),
            }"
            :key="b.id"
            @click="runCommand(b.command)"
          >
            <v-icon>{{ b.icon }}</v-icon>
          </v-btn>
        </v-btn-toggle>
        <v-btn-toggle dense light borderless>
          <v-btn
            :small="small"
            v-for="b in buttonGroupThree"
            :title="b.title"
            :key="b.id"
            @click="runCommand(b.command)"
          >
            <v-icon>{{ b.icon }}</v-icon>
          </v-btn>
          <v-btn v-if="!small" title="Maximera" key="maximize" @click="onResize">
            <v-icon>{{ maxmimizedUI ? 'mdi-arrow-collapse' : 'mdi-arrow-expand' }}</v-icon>
          </v-btn>
        </v-btn-toggle>
      </div>
    </template>

    <floating-menu
      v-if="editor"
      :editor="editor"
      :tippy-options="{ duration: 100 }"
      :shouldShow="shouldShowFloatingMenu"
    >
      <v-btn-toggle rounded dense light borderless>
        <v-btn
          small
          v-for="b in [...buttonGroupOne, ...buttonGroupTwo]"
          :key="b.id"
          @click="runCommand(b.command)"
        >
          <v-icon>{{ b.icon }}</v-icon>
        </v-btn>
        <v-btn small @click="addImageMedia">
          <v-icon>mdi-multimedia</v-icon>
        </v-btn>
        <v-btn small @click="addAccordion">
          <v-icon>mdi-arrow-expand-vertical</v-icon>
        </v-btn>
        <v-btn small @click="addPrettyAnchor">
          <v-icon>mdi-link-variant</v-icon>
        </v-btn>
      </v-btn-toggle>
    </floating-menu>

    <bubble-menu rounded :editor="editor" :tippy-options="{ duration: 100 }" v-if="editor">
      <v-btn-toggle dark>
        <v-btn
          small
          :class="{
            'v-item--active v-btn--active':
              'isActive' in b && editor.isActive(b.isActive.name, b.isActive.params),
          }"
          v-for="b in buttonGroupFour"
          :key="b.id"
          @click="runCommand(b.command)"
        >
          <v-icon small>{{ b.icon }}</v-icon>
        </v-btn>
        <v-btn small :class="{ 'is-active': editor.isActive('link') }" @click="setLink">
          <v-icon small>mdi-link</v-icon>
        </v-btn>
      </v-btn-toggle>
    </bubble-menu>
    <editor-content class="editor__content" :editor="editor" />
    <InsertImageDialog ref="InsertImageDialog" />
    <InsertLinkDialog ref="InsertLinkDialog" />
  </v-card>
</template>

<script>
import unescape from 'lodash/unescape'
import { Editor, EditorContent, FloatingMenu, BubbleMenu } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import { buttonGroupOne, buttonGroupTwo, buttonGroupThree, buttonGroupFour } from './buttonGroups'
import InsertImageDialog from './InsertImageDialog'
import InsertLinkDialog from './InsertLinkDialog'
import VideoElement from '@/components/shared/editor/VideoElement.js'
import Summary from '@/components/shared/editor/extensions/Summary.js'
import PrettyAnchor from '@/components/shared/editor/extensions/PrettyAnchor.js'

export default {
  props: {
    data: {
      type: String,
    },
    theme: {
      type: String,
    },
    small: {
      type: Boolean,
      default: false,
    },
    maxmimizedUI: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    EditorContent,
    FloatingMenu,
    BubbleMenu,
    InsertImageDialog,
    InsertLinkDialog,
  },
  data() {
    return {
      keepInBounds: true,
      editor: null,
      saving: false,
      overlay: true,
      linkUrl: null,
      linkMenuIsActive: false,
      buttonGroupOne,
      buttonGroupTwo,
      buttonGroupThree,
      buttonGroupFour,
    }
  },

  mounted() {
    const self = this
    this.editor = new Editor({
      onUpdate({ editor }) {
        self.$emit('editor:update', { data: editor.getHTML() })
      },
      extensions: [
        StarterKit.configure({
          history: true,
          heading: {
            levels: [1, 2, 3, 4, 5],
          },
        }),
        Image,
        Underline,
        VideoElement,
        Summary,
        PrettyAnchor,
        Link,
      ],
      autoFocus: true,
      content: unescape(this.data),
    })
    this.overlay = false
  },
  beforeDestroy() {
    this.editor.destroy()
  },
  computed: {},
  methods: {
    shouldShowFloatingMenu({ editor, view, state }) {
      const { selection } = state
      const { $anchor, empty } = selection
      const isRootDepth = $anchor.depth === 1
      const isEmptyTextBlock =
        $anchor.parent.isTextblock && !$anchor.parent.type.spec.code && !$anchor.parent.textContent

      const isSummaryComponent = editor.isActive('HavetDetails')

      if (
        !view.hasFocus() ||
        !empty ||
        (!isRootDepth && !isSummaryComponent) ||
        !isEmptyTextBlock ||
        !this.editor.isEditable
      ) {
        return false
      }

      return true
    },
    addBildText(content) {
      this.editor.chain().createParagraphNear().insertContent('<h4>Bildtext</h4>').run()
    },
    onResize() {
      this.$emit('editor:resize', !this.maxmimizedUI)
    },
    async runCommand({ name, params }) {
      if (!name) return
      await this.editor.chain().focus()[name](params).run()
    },
    async setLink() {
      const previousUrl = this.editor.getAttributes('link').href
      const url = await this.$refs.InsertLinkDialog.open({
        previousUrl,
      })

      // cancelled
      if (url === null) {
        return
      }

      // empty
      if (url === '') {
        this.editor.chain().focus().extendMarkRange('link').unsetLink().run()
        return
      }
      // update link
      this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    },

    async addImageMedia() {
      const { url, alt, type } = await this.$refs.InsertImageDialog.open({
        title: 'Lägg till media',
        message: 'Skriv url till bilden du vill lägga in.',
        label: 'Url',
      })

      if (url) {
        if (type === 'video') {
          this.editor.chain().focus().insertContent(`<video src="${url}"></video>`).run()
          return
        }
        this.editor.chain().focus().setImage({ src: url, alt }).run()
        this.addBildText()
      }
    },
    addAccordion() {
      this.editor.chain().focus().insertContent('<details><p></p></details>').run()
    },
    addPrettyAnchor() {
      this.editor
        .chain()
        .focus()
        .insertContent(
          '<a class="havet-pretty-anchor"><p><strong>Rubrik</strong></p><p>Beskrivning</p></a>'
        )
        .run()
    },
  },
}
</script>

<style lang="less" scoped>
.havet-editor {
  margin: auto;
  position: relative;

  :focus {
    outline: -webkit-focus-ring-color auto 0px;
  }
  .editor-menu-bar {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
  }
  .editor-menu-bar-small {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 4px;
  }
}
</style>
